import React, { useLayoutEffect, useMemo, useRef, useState } from "react"
import ReactApexChart from "react-apexcharts"
import PPUtils from "../../utils/PPUtils"
import PPPictureBoxLoad from "../pricetable/PPPictureBoxLoad"
import "../../styles/misc.css"

function toMillis(value) {
    if (value == null) {
        return null
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        return value < 1e12 ? value * 1000 : value
    }
    const numeric = Number(value)
    if (Number.isFinite(numeric) && numeric > 0) {
        return numeric < 1e12 ? numeric * 1000 : numeric
    }
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : null
}

function trendColors(trend) {
    if (trend > 0) {
        return { line: PPUtils.Colors.GreenStart, fill: PPUtils.Colors.GreenEnd }
    }
    if (trend < 0) {
        return { line: PPUtils.Colors.RedStart, fill: PPUtils.Colors.RedEnd }
    }
    return { line: PPUtils.Colors.NeutralStart, fill: PPUtils.Colors.NeutralEnd }
}

function PPHotProductItem({ product, t, i18n, tariffComparisonMode, selectedShopId, selectedShop }) {
    const chartWrapRef = useRef(null)
    const [chartWidth, setChartWidth] = useState(0)

    useLayoutEffect(() => {
        const el = chartWrapRef.current
        if (!el) {
            return undefined
        }
        const applyWidth = () => {
            const next = Math.floor(el.getBoundingClientRect().width)
            if (next > 16) {
                setChartWidth((prev) => (prev === next ? prev : next))
            }
        }
        applyWidth()
        const observer = new ResizeObserver(applyWidth)
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    const seriesData = useMemo(() => {
        const points = []
        for (const entry of product.lastPriceChangesOverAllShops || []) {
            if (entry?.price == null || entry?.crawlDate == null) {
                continue
            }
            if (selectedShopId != null) {
                const matchById = entry.shopId != null && Number(entry.shopId) === Number(selectedShopId)
                const matchByName =
                    selectedShop != null &&
                    entry.shopName === selectedShop.name &&
                    entry.lang === selectedShop.lang
                if (!matchById && !matchByName) {
                    continue
                }
            }
            const x = toMillis(entry.crawlDate)
            if (x == null) {
                continue
            }
            points.push({
                x,
                y: Number(entry.price),
                shopName: entry.shopName,
                lang: entry.lang
            })
        }
        points.sort((a, b) => a.x - b.x)
        return points
    }, [product.lastPriceChangesOverAllShops, selectedShop, selectedShopId])

    const colors = trendColors(product.priceTrendToCustomerPrice)

    const options = useMemo(
        () => ({
            chart: {
                type: "area",
                toolbar: { show: false },
                zoom: { enabled: false },
                parentHeightOffset: 0,
                animations: { enabled: false },
                redrawOnParentResize: true,
                redrawOnWindowResize: true,
                width: chartWidth || undefined,
                height: 125
            },
            stroke: {
                width: 2,
                curve: "smooth"
            },
            grid: {
                show: false,
                padding: {
                    top: 12,
                    right: 12,
                    bottom: 8,
                    left: 8
                }
            },
            fill: {
                type: "gradient",
                gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.45,
                    opacityTo: 0.1,
                    stops: [0, 90, 100],
                    colorStops: [
                        { offset: 0, color: colors.fill, opacity: 0.45 },
                        { offset: 100, color: colors.fill, opacity: 0.08 }
                    ]
                }
            },
            dataLabels: {
                enabled: false
            },
            markers: {
                size: seriesData.length < 2 ? 5 : 0
            },
            xaxis: {
                type: "datetime",
                labels: { show: false },
                axisBorder: { show: false },
                axisTicks: { show: false },
                tooltip: { enabled: false }
            },
            yaxis: {
                labels: { show: false }
            },
            legend: { show: false },
            tooltip: {
                custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                    const point = w.config.series[0].data[dataPointIndex]
                    const when = new Intl.DateTimeFormat(i18n.language, {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit"
                    }).format(new Date(point.x))
                    return (
                        '<div class="custom_tooltip">' +
                        '<div class="tooltip_headline">' +
                        when +
                        '</div><div class="tooltip_content"><p>' +
                        t("single.price") +
                        ": <strong>" +
                        series[seriesIndex][dataPointIndex] +
                        "€</strong><br>Shop: <strong>" +
                        (point.shopName || "") +
                        " (" +
                        (point.lang || "") +
                        ")</strong></p>" +
                        "</div>"
                    )
                }
            },
            colors: [colors.line]
        }),
        [chartWidth, colors.fill, colors.line, i18n.language, seriesData.length, t]
    )

    const series = useMemo(() => [{ name: "Preis", data: seriesData }], [seriesData])

    let price_difference = 0
    if (product.price > 0 && product.minPrice > 0) {
        price_difference = PPUtils.getPriceDifference(product.price, product.minPrice).toFixed(2)
    }

    const productHref = `/pricetable/s/${encodeURIComponent(product.productnr.replace(/\//g, "||"))}`

    return (
        <div className="row border-bottom my-1 hotProductItemBox">
            <div className="col-2 text-center hotProductItemBoxImg">
                <div className="productimgbox dashboard">
                    <a href={productHref} className="hotProductItemLink">
                        <PPPictureBoxLoad
                            image={product.productImage}
                            className={"img-fluid"}
                            alt={product.productName}
                        />
                    </a>
                </div>
            </div>
            <div className="col-4 hotProductItemBoxText">
                <h2 className="lead">
                    <a href={productHref} className="hotProductItemLink">
                        <b>{product.productName}</b>
                    </a>
                </h2>
                <p className="text-muted text-sm mb-1">
                    <b>
                        {PPUtils.isTariffComparisonMode(tariffComparisonMode)
                            ? PPUtils.getTariffComparisonModeProducerString(t)
                            : t("single.manufacturer")}
                        :
                    </b>{" "}
                    {product.productProducer}
                </p>
                <p className="text-muted text-sm mb-1">
                    <b>{t("single.productnr")}:</b> {product.productnr}
                </p>
                {product.price > 0 && (
                    <>
                        <p className="text-muted text-sm mb-1">
                            <b>{t("single.price")}:</b> {Number(PPUtils.addZeroesIfNeeded(product.price)).toFixed(2)} €
                        </p>
                        <p className="text-muted text-sm mb-1">
                            <b>{t("single.price_min")}</b>{" "}
                            {Number(PPUtils.addZeroesIfNeeded(product.minPrice)).toFixed(2)} €
                        </p>
                        <p className="text-muted text-sm mb-1">
                            <b>{t("single.price_difference")}</b> {product.minPrice > 0 ? price_difference + " %" : "-"}
                        </p>
                    </>
                )}
            </div>
            <div className="col-6 hotProductItemBoxChart" ref={chartWrapRef}>
                {seriesData.length > 0 && chartWidth > 16 ? (
                    <ReactApexChart
                        options={options}
                        series={series}
                        type="area"
                        height={125}
                        width={chartWidth}
                    />
                ) : null}
            </div>
        </div>
    )
}

export default PPHotProductItem
